import * as React from "react";
import { useTxInOutMatrixRange } from "@/hooks/finance/reconciliation/useBankTransactionsInAndOut";

type Args = {
  bankAccountIds: string[];
  dateFrom: string; // YYYY-MM-DD
  dateTo: string;   // YYYY-MM-DD
  enabled?: boolean;
};

export type FeesRow = {
  fee_total: number;
  tx_count: number;
  currency: string | null;
};

export type FeesMatrix = {
  dateKeys: string[];
  byAccount: Map<string, Map<string, FeesRow>>;
};

export function useFeesMatrixRange(args: Args) {
  const { bankAccountIds, dateFrom, dateTo, enabled = true } = args;

  const txQ = useTxInOutMatrixRange({
    bankAccountIds,
    dateFrom,
    dateTo,
    enabled,
  });

  const data = React.useMemo<FeesMatrix | undefined>(() => {
    if (!txQ.data) return undefined;

    const byAccount = new Map<string, Map<string, FeesRow>>();

    for (const accId of bankAccountIds) {
      const srcMap = txQ.data.byAccount.get(accId);
      const outMap = new Map<string, FeesRow>();

      for (const dayKey of txQ.data.dateKeys) {
        const row = srcMap?.get(dayKey);

        outMap.set(dayKey, {
          fee_total: row?.fee_total ?? 0,
          tx_count: row?.tx_count ?? 0,
          currency: row?.currency ?? null,
        });
      }

      byAccount.set(accId, outMap);
    }

    return {
      dateKeys: txQ.data.dateKeys,
      byAccount,
    };
  }, [txQ.data, bankAccountIds]);

  return {
    data,
    isLoading: txQ.isLoading,
    isError: txQ.isError,
    error: txQ.error ?? null,
    refetch: txQ.refetch,
  };
}